/**
 * skaile setup — Interactive provider setup wizard
 *
 * Discovers existing config, lets the user pick providers, enter/validate API keys,
 * choose where to store them, and set defaults.
 */

import fs from "node:fs";
import os from "node:os";
import path from "node:path";
import * as p from "@clack/prompts";
import { getModels, validateApiKey } from "@skaile/workspaces/bridge";
import {
  ALL_PROVIDERS,
  detectEnvApiKeys,
  loadSettings,
  maskApiKey,
  projectSettingsPath,
  providerEnvKey,
  resolveSettings,
  type SkaileSettings,
  saveSettings,
  WorkspaceYamlEditor,
} from "@skaile/workspaces/core";

// ---------------------------------------------------------------------------
// Provider metadata
// ---------------------------------------------------------------------------

/** LLM providers only (excludes voice providers) */
const LLM_PROVIDERS = ALL_PROVIDERS.filter((p) => p !== "deepgram" && p !== "elevenlabs");

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI (GPT)",
  google: "Google (Gemini)",
  mistral: "Mistral AI",
  groq: "Groq (fast inference)",
  openrouter: "OpenRouter (multi-provider gateway)",
  deepseek: "DeepSeek",
  xai: "xAI (Grok)",
  together: "Together AI",
  fireworks: "Fireworks AI",
};

const PROVIDER_KEY_URLS: Record<string, string> = {
  anthropic: "https://console.anthropic.com/settings/keys",
  openai: "https://platform.openai.com/api-keys",
  google: "https://aistudio.google.com/apikey",
  mistral: "https://console.mistral.ai/api-keys",
  groq: "https://console.groq.com/keys",
  openrouter: "https://openrouter.ai/settings/keys",
  deepseek: "https://platform.deepseek.com/api_keys",
  xai: "https://console.x.ai/",
  together: "https://api.together.ai/settings/api-keys",
  fireworks: "https://fireworks.ai/api-keys",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StorageTarget = "settings" | "env-project" | "env-global" | "skip";
type DefaultsTarget = "project" | "personal";

interface SetupPlan {
  providers: string[];
  newKeys: Record<string, string>;
  storageTarget: StorageTarget;
  defaultsTarget: DefaultsTarget;
  defaultProvider: string;
  defaultModel: string;
  defaultDriver: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isCancel(value: unknown): value is symbol {
  return p.isCancel(value);
}

function exitOnCancel<T>(value: T): asserts value is Exclude<T, symbol> {
  if (isCancel(value)) {
    p.cancel("Setup cancelled.");
    process.exit(0);
  }
}

/** Load .env files from common locations and parse key=value pairs */
function loadDotenvKeys(): Record<string, string> {
  const locations = [path.join(process.cwd(), ".env"), path.join(os.homedir(), ".env")];
  const result: Record<string, string> = {};
  for (const loc of locations) {
    try {
      const content = fs.readFileSync(loc, "utf-8");
      for (const line of content.split("\n")) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith("#")) continue;
        const eq = trimmed.indexOf("=");
        if (eq === -1) continue;
        const key = trimmed.slice(0, eq).trim();
        let val = trimmed.slice(eq + 1).trim();
        // Strip surrounding quotes
        if (
          (val.startsWith('"') && val.endsWith('"')) ||
          (val.startsWith("'") && val.endsWith("'"))
        ) {
          val = val.slice(1, -1);
        }
        result[key] = val;
      }
    } catch {
      // file doesn't exist, skip
    }
  }
  return result;
}

/** Append key=value entries to a .env file without overwriting existing keys */
function appendEnvFile(filePath: string, entries: Record<string, string>): void {
  let existing = "";
  try {
    existing = fs.readFileSync(filePath, "utf-8");
  } catch {
    // file doesn't exist yet
  }

  const existingKeys = new Set<string>();
  for (const line of existing.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const eq = trimmed.indexOf("=");
    if (eq !== -1) existingKeys.add(trimmed.slice(0, eq).trim());
  }

  const lines: string[] = [];
  for (const [key, value] of Object.entries(entries)) {
    if (existingKeys.has(key)) {
      p.log.warn(`  ${key} already exists in ${filePath} — skipping`);
      continue;
    }
    lines.push(`${key}=${value}`);
  }

  if (lines.length === 0) return;

  const separator = existing.endsWith("\n") || existing === "" ? "" : "\n";
  const content = `${separator + lines.join("\n")}\n`;
  fs.mkdirSync(path.dirname(filePath), { recursive: true });
  fs.appendFileSync(filePath, content, "utf-8");
}

// ---------------------------------------------------------------------------
// Wizard steps
// ---------------------------------------------------------------------------

async function showCurrentState(
  settings: SkaileSettings,
  envKeys: Record<string, string>,
): Promise<void> {
  const apiKeys = settings.apiKeys ?? {};
  const lines: string[] = ["Provider keys:"];
  for (const provider of LLM_PROVIDERS) {
    const envVal = envKeys[provider];
    const storedVal = apiKeys[provider];
    if (envVal) {
      lines.push(`  ${provider.padEnd(12)} [env]    ${maskApiKey(envVal)}`);
    } else if (storedVal) {
      lines.push(`  ${provider.padEnd(12)} [stored] ${maskApiKey(storedVal)}`);
    } else {
      lines.push(`  ${provider.padEnd(12)} (not configured)`);
    }
  }
  lines.push("");
  lines.push("Current defaults:");
  lines.push(`  Provider: ${settings.provider ?? "(not set)"}`);
  lines.push(`  Model:    ${settings.model ?? "(not set)"}`);
  lines.push(`  Driver:   ${settings.driver ?? "(not set)"}`);

  p.note(lines.join("\n"), "Current configuration");
}

async function selectProviders(
  envKeys: Record<string, string>,
  storedKeys: Record<string, string>,
): Promise<string[]> {
  const result = await p.multiselect({
    message: "Which providers do you want to configure?",
    options: LLM_PROVIDERS.map((id) => ({
      value: id,
      label: PROVIDER_LABELS[id] ?? id,
      hint: envKeys[id]
        ? `env: ${maskApiKey(envKeys[id])}`
        : storedKeys[id]
          ? `stored: ${maskApiKey(storedKeys[id])}`
          : undefined,
    })),
    initialValues: LLM_PROVIDERS.filter((id) => envKeys[id] || storedKeys[id]),
    required: true,
  });
  exitOnCancel(result);
  return result as string[];
}

/**
 * Run the optional validation step for a freshly entered key.
 * Returns true if the key should be kept (valid, or user opted to keep anyway),
 * false if it should be discarded.
 */
async function confirmKeyAfterValidation(provider: string, key: string): Promise<boolean> {
  const shouldValidate = await p.confirm({
    message: "Validate this key?",
    initialValue: true,
  });
  exitOnCancel(shouldValidate);

  if (!shouldValidate) return true;

  const s = p.spinner();
  s.start(`Validating ${provider} key...`);
  const result = await validateApiKey(provider, key);
  if (result.ok) {
    s.stop(`${provider} key is valid`);
    return true;
  }
  s.stop(`${provider} validation failed: ${result.error}`);
  const retry = await p.confirm({
    message: "Use this key anyway?",
    initialValue: false,
  });
  exitOnCancel(retry);
  return retry;
}

/**
 * Collect (or skip) the API key for a single provider.
 * Returns the entered key, or undefined when the provider is already configured,
 * the user declines to update, leaves it blank, or rejects a failed validation.
 */
async function collectProviderKey(
  provider: string,
  envKeys: Record<string, string>,
  storedKeys: Record<string, string>,
): Promise<string | undefined> {
  const envVar = providerEnvKey(provider);

  if (envKeys[provider]) {
    p.log.info(`${PROVIDER_LABELS[provider] ?? provider}: already set via ${envVar}`);
    return undefined;
  }

  if (storedKeys[provider]) {
    const update = await p.confirm({
      message: `${PROVIDER_LABELS[provider] ?? provider}: key already stored (${maskApiKey(storedKeys[provider])}). Update it?`,
      initialValue: false,
    });
    exitOnCancel(update);
    if (!update) return undefined;
  }

  const keyUrl = PROVIDER_KEY_URLS[provider];
  const key = await p.password({
    message: `${PROVIDER_LABELS[provider] ?? provider} API key${keyUrl ? ` (get one at ${keyUrl})` : ""}:`,
  });
  exitOnCancel(key);

  if (!key) return undefined;

  const keep = await confirmKeyAfterValidation(provider, key);
  if (!keep) return undefined;

  return key;
}

async function collectApiKeys(
  providers: string[],
  envKeys: Record<string, string>,
  storedKeys: Record<string, string>,
): Promise<Record<string, string>> {
  const newKeys: Record<string, string> = {};

  for (const provider of providers) {
    const key = await collectProviderKey(provider, envKeys, storedKeys);
    if (key !== undefined) newKeys[provider] = key;
  }

  return newKeys;
}

async function selectStorageTarget(): Promise<StorageTarget> {
  const result = await p.select({
    message: "Where should API keys be stored?",
    options: [
      { value: "settings" as const, label: ".skaile/settings.json (project-local, gitignored)" },
      { value: "env-project" as const, label: `${process.cwd()}/.env (project-level)` },
      { value: "env-global" as const, label: `${os.homedir()}/.env (global)` },
      { value: "skip" as const, label: "Skip — I'll set env vars myself" },
    ],
  });
  exitOnCancel(result);
  return result as StorageTarget;
}

async function selectDefaultProvider(providers: string[], current?: string): Promise<string> {
  const result = await p.select({
    message: "Default LLM provider:",
    options: providers.map((id) => ({
      value: id,
      label: PROVIDER_LABELS[id] ?? id,
    })),
    initialValue: current && providers.includes(current) ? current : providers[0],
  });
  exitOnCancel(result);
  return result as string;
}

async function selectDefaultModel(
  provider: string,
  current?: string,
  apiKey?: string,
): Promise<string> {
  // Try live model list from provider API, fall back to static catalog
  const s = apiKey ? p.spinner() : undefined;
  if (s) s.start(`Fetching models from ${provider}...`);

  const fetchResult = await getModels(provider, apiKey);

  if (s) {
    if (fetchResult.source === "api") {
      s.stop(`Loaded ${fetchResult.models.length} models from ${provider} API`);
    } else if (fetchResult.error) {
      s.stop(`Could not fetch live models (${fetchResult.error}) — using built-in catalog`);
    } else {
      s.stop(`Using built-in model catalog`);
    }
  }

  const models = fetchResult.models;
  if (models.length === 0) {
    p.log.warn(`No models found for ${provider} — enter a model ID manually`);
    const result = await p.text({
      message: "Model ID:",
      initialValue: current ?? "",
    });
    exitOnCancel(result);
    return result as string;
  }

  const result = await p.select({
    message: `Default model (${provider}):`,
    options: models.map((m) => ({
      value: m.id,
      label: `${m.name} (${m.id})`,
    })),
    initialValue: current && models.some((m) => m.id === current) ? current : models[0].id,
  });
  exitOnCancel(result);
  return result as string;
}

async function selectDriver(current?: string): Promise<string> {
  const result = await p.select({
    message: "Default agent driver:",
    options: [
      { value: "omp", label: "omp (oh-my-pi — multi-provider, recommended)" },
      { value: "claude-sdk", label: "claude-sdk (Anthropic Agent SDK, Claude only)" },
      { value: "codex", label: "codex (OpenAI Codex SDK, OpenAI only)" },
    ],
    initialValue: current ?? "omp",
  });
  exitOnCancel(result);
  return result as string;
}

async function selectDefaultsTarget(projectDir: string): Promise<DefaultsTarget> {
  const yamlPath = path.join(projectDir, "skaile.yaml");
  const hasYaml = fs.existsSync(yamlPath);

  if (!hasYaml) {
    // No skaile.yaml — can only save personally
    return "personal";
  }

  const result = await p.select({
    message: "Where should driver, provider, and model defaults be saved?",
    options: [
      {
        value: "project" as const,
        label: "skaile.yaml (project-wide, shared with team)",
        hint: "recommended",
      },
      {
        value: "personal" as const,
        label: ".skaile/settings.json (personal only, gitignored)",
      },
    ],
    initialValue: "project" as const,
  });
  exitOnCancel(result);
  return result as DefaultsTarget;
}

/** Build the human-readable summary shown before applying the plan. */
function buildSummaryLines(plan: SetupPlan, settingsFile: string): string[] {
  const lines: string[] = [];

  if (Object.keys(plan.newKeys).length > 0) {
    lines.push("New API keys:");
    for (const [provider, key] of Object.entries(plan.newKeys)) {
      lines.push(`  ${provider.padEnd(12)} ${maskApiKey(key)}`);
    }
    const targetLabels: Record<StorageTarget, string> = {
      settings: `.skaile/settings.json (${settingsFile})`,
      "env-project": `${process.cwd()}/.env`,
      "env-global": `${os.homedir()}/.env`,
      skip: "not saved (set env vars manually)",
    };
    lines.push(`  Storage:   ${targetLabels[plan.storageTarget]}`);
    lines.push("");
  }

  const defaultsLabel =
    plan.defaultsTarget === "project"
      ? "skaile.yaml (project-wide)"
      : ".skaile/settings.json (personal)";
  lines.push("Defaults:");
  lines.push(`  Provider:  ${plan.defaultProvider}`);
  lines.push(`  Model:     ${plan.defaultModel}`);
  lines.push(`  Driver:    ${plan.defaultDriver}`);
  lines.push(`  Saved to:  ${defaultsLabel}`);

  return lines;
}

/**
 * Write the new API keys to the chosen storage target.
 * For the `settings` target the keys are merged into `merged.apiKeys` (mutated
 * in place); for the env targets they are appended to the relevant `.env` file.
 */
function persistApiKeys(plan: SetupPlan, merged: SkaileSettings): void {
  const hasNewKeys = Object.keys(plan.newKeys).length > 0;

  if (hasNewKeys && plan.storageTarget === "settings") {
    const apiKeys = { ...(merged.apiKeys ?? {}) };
    for (const [provider, key] of Object.entries(plan.newKeys)) {
      apiKeys[provider] = key;
    }
    merged.apiKeys = apiKeys;
  } else if (hasNewKeys && plan.storageTarget === "env-project") {
    const envEntries: Record<string, string> = {};
    for (const [provider, key] of Object.entries(plan.newKeys)) {
      envEntries[providerEnvKey(provider)] = key;
    }
    appendEnvFile(path.join(process.cwd(), ".env"), envEntries);
    p.log.success(`Appended keys to ${process.cwd()}/.env`);
  } else if (hasNewKeys && plan.storageTarget === "env-global") {
    const envEntries: Record<string, string> = {};
    for (const [provider, key] of Object.entries(plan.newKeys)) {
      envEntries[providerEnvKey(provider)] = key;
    }
    appendEnvFile(path.join(os.homedir(), ".env"), envEntries);
    p.log.success(`Appended keys to ${os.homedir()}/.env`);
  }
}

/**
 * Apply the plan's defaults: write them to skaile.yaml when saving project-wide,
 * and set/strip the corresponding personal settings fields on `merged` (mutated
 * in place) so they don't shadow project-wide values.
 */
function applyDefaults(plan: SetupPlan, projectDir: string, merged: SkaileSettings): void {
  // Save defaults to skaile.yaml or settings.json depending on user choice
  if (plan.defaultsTarget === "project") {
    const yamlPath = path.join(projectDir, "skaile.yaml");
    const editor = WorkspaceYamlEditor.load(yamlPath);
    editor.setAgentConfig("default", {
      provider: plan.defaultProvider,
      model: plan.defaultModel,
      driver: plan.defaultDriver,
    });
    editor.save();
    p.log.success(`Saved defaults to ${yamlPath}`);
  }

  // Personal settings: only include defaults if saving personally, always include API keys.
  // When saving to project (skaile.yaml), strip driver/provider/model from settings.json
  // so they don't shadow the project-wide values.
  if (plan.defaultsTarget === "personal") {
    merged.provider = plan.defaultProvider;
    merged.model = plan.defaultModel;
    merged.driver = plan.defaultDriver;
  } else {
    delete merged.provider;
    delete merged.model;
    delete merged.driver;
  }
}

async function reviewAndSave(plan: SetupPlan, projectDir: string): Promise<void> {
  const settingsFile = projectSettingsPath(projectDir);

  p.note(buildSummaryLines(plan, settingsFile).join("\n"), "Summary");

  const confirmed = await p.confirm({
    message: "Apply these settings?",
    initialValue: true,
  });
  exitOnCancel(confirmed);

  if (!confirmed) {
    p.cancel("Setup cancelled — no changes were made.");
    process.exit(0);
  }

  // Load existing project settings (or empty)
  const existing = await loadSettings(settingsFile);

  const merged: SkaileSettings = { ...existing };
  applyDefaults(plan, projectDir, merged);
  persistApiKeys(plan, merged);

  // Write unified settings file
  await saveSettings(merged, settingsFile);
  p.log.success(`Saved settings to ${settingsFile}`);
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

/** Check whether Claude Code OAuth credentials exist on disk */
function hasClaudeCodeCredentials(): boolean {
  const credPath = path.join(os.homedir(), ".claude", ".credentials.json");
  try {
    const content = fs.readFileSync(credPath, "utf-8");
    const parsed = JSON.parse(content);
    // Check that it has at least one non-empty field
    return parsed && (parsed.accessToken || parsed.refreshToken || parsed.oauthToken);
  } catch {
    return false;
  }
}

/** The provider/key/model portion of a SetupPlan produced by a per-driver flow. */
interface DriverSetupResult {
  providers: string[];
  newKeys: Record<string, string>;
  storageTarget: StorageTarget;
  defaultProvider: string;
  defaultModel: string;
}

/**
 * Claude SDK path: Anthropic only. Honours an existing API key (env or stored)
 * or Claude Code OAuth credentials, otherwise offers to enter a key.
 */
async function runClaudeSdkSetup(
  settings: SkaileSettings,
  envKeys: Record<string, string>,
  apiKeys: Record<string, string>,
): Promise<DriverSetupResult> {
  const defaultProvider = "anthropic";
  let newKeys: Record<string, string>;
  let storageTarget: StorageTarget = "skip";

  // Check for existing auth: API key or Claude Code OAuth credentials
  const hasEnvKey = !!envKeys.anthropic;
  const hasStoredKey = !!apiKeys.anthropic;
  const hasOAuth = hasClaudeCodeCredentials();

  if (hasEnvKey) {
    p.log.info(`Anthropic API key found in environment (${maskApiKey(envKeys.anthropic)})`);
    newKeys = {};
  } else if (hasStoredKey) {
    p.log.info(`Anthropic API key found in settings (${maskApiKey(apiKeys.anthropic)})`);
    newKeys = {};
  } else if (hasOAuth) {
    p.log.info("Claude Code subscription detected (OAuth credentials found)");
    p.log.info("The Claude SDK will use your Claude Code login — no API key needed.");
    newKeys = {};
  } else {
    // No auth found — prompt for Anthropic API key
    p.log.warn("No Anthropic API key or Claude Code subscription found.");
    p.log.info("You can either:");
    p.log.info("  1. Enter an Anthropic API key now");
    p.log.info("  2. Run `claude login` to authenticate with Claude Code");

    const wantsKey = await p.confirm({
      message: "Enter an Anthropic API key?",
      initialValue: true,
    });
    exitOnCancel(wantsKey);

    if (wantsKey) {
      newKeys = await collectApiKeys(["anthropic"], envKeys, apiKeys);
    } else {
      newKeys = {};
    }
  }

  // Storage target (only ask if there are new keys)
  if (Object.keys(newKeys).length > 0) {
    storageTarget = await selectStorageTarget();
  }

  // Model selection — only Anthropic models (try live fetch with available key)
  const anthropicKey = newKeys.anthropic ?? envKeys.anthropic ?? apiKeys.anthropic;
  const defaultModel = await selectDefaultModel("anthropic", settings.model, anthropicKey);

  return { providers: ["anthropic"], newKeys, storageTarget, defaultProvider, defaultModel };
}

/** Codex path: OpenAI only. Honours an existing key, otherwise prompts for one. */
async function runCodexSetup(
  settings: SkaileSettings,
  envKeys: Record<string, string>,
  apiKeys: Record<string, string>,
): Promise<DriverSetupResult> {
  const defaultProvider = "openai";
  let newKeys: Record<string, string>;
  let storageTarget: StorageTarget = "skip";

  const hasEnvKey = !!envKeys.openai;
  const hasStoredKey = !!apiKeys.openai;

  if (hasEnvKey) {
    p.log.info(`OpenAI API key found in environment (${maskApiKey(envKeys.openai)})`);
    newKeys = {};
  } else if (hasStoredKey) {
    p.log.info(`OpenAI API key found in settings (${maskApiKey(apiKeys.openai)})`);
    newKeys = {};
  } else {
    p.log.warn("No OpenAI API key found.");
    newKeys = await collectApiKeys(["openai"], envKeys, apiKeys);
  }

  if (Object.keys(newKeys).length > 0) {
    storageTarget = await selectStorageTarget();
  }

  const openaiKey = newKeys.openai ?? envKeys.openai ?? apiKeys.openai;
  const defaultModel = await selectDefaultModel("openai", settings.model, openaiKey);

  return { providers: ["openai"], newKeys, storageTarget, defaultProvider, defaultModel };
}

/** omp / pi path: multi-provider flow (existing behavior). */
async function runMultiProviderSetup(
  settings: SkaileSettings,
  envKeys: Record<string, string>,
  apiKeys: Record<string, string>,
): Promise<DriverSetupResult> {
  let storageTarget: StorageTarget = "skip";

  // 4. Select providers
  const providers = await selectProviders(envKeys, apiKeys);

  // 5. Collect API keys
  const newKeys = await collectApiKeys(providers, envKeys, apiKeys);

  // 6. Storage target (only ask if there are new keys)
  if (Object.keys(newKeys).length > 0) {
    storageTarget = await selectStorageTarget();
  }

  // 7. Default provider (from selected providers + any already configured)
  const allConfigured = [
    ...new Set([
      ...providers,
      ...Object.keys(envKeys),
      ...Object.keys(apiKeys).filter((k) => apiKeys[k]),
    ]),
  ].filter((p) => LLM_PROVIDERS.includes(p as any));
  const defaultProvider = await selectDefaultProvider(
    allConfigured.length > 0 ? allConfigured : providers,
    settings.provider,
  );

  // 8. Default model (try live fetch with available key)
  const providerKey =
    newKeys[defaultProvider] ?? envKeys[defaultProvider] ?? apiKeys[defaultProvider];
  const defaultModel = await selectDefaultModel(defaultProvider, settings.model, providerKey);

  return { providers, newKeys, storageTarget, defaultProvider, defaultModel };
}

/**
 * Interactive provider setup wizard: discovers existing config, prompts for
 * provider/model/driver preferences and API keys, then writes the results to
 * `skaile.yaml` or `.skaile/settings.json` depending on the user's choice.
 *
 * @param _args - Unused positional args (reserved for future use).
 * @param opts - Options including an optional `projectDir` override.
 * @docLink cli/dev-guide#setup
 */
export async function cmdSetup(_args: string[], opts: { projectDir?: string }): Promise<void> {
  p.intro("skaile setup");

  const projectDir = opts.projectDir ?? process.cwd();

  // 1. Detect existing state via layered resolution
  const dotenvKeys = loadDotenvKeys();
  const envKeys = detectEnvApiKeys(dotenvKeys);
  const settings = await resolveSettings(projectDir);

  // 2. Show current state
  await showCurrentState(settings, envKeys);

  // 3. Select driver FIRST — it determines which providers/models are relevant
  const defaultDriver = await selectDriver(settings.driver);

  const apiKeys = settings.apiKeys ?? {};

  // Per-driver provider/key/model flow
  let result: DriverSetupResult;
  if (defaultDriver === "claude-sdk") {
    result = await runClaudeSdkSetup(settings, envKeys, apiKeys);
  } else if (defaultDriver === "codex") {
    result = await runCodexSetup(settings, envKeys, apiKeys);
  } else {
    result = await runMultiProviderSetup(settings, envKeys, apiKeys);
  }

  // 9. Choose where to save defaults (project-wide vs personal)
  const defaultsTarget = await selectDefaultsTarget(projectDir);

  // 10. Review and save
  await reviewAndSave(
    {
      providers: result.providers,
      newKeys: result.newKeys,
      storageTarget: result.storageTarget,
      defaultsTarget,
      defaultProvider: result.defaultProvider,
      defaultModel: result.defaultModel,
      defaultDriver,
    },
    projectDir,
  );

  p.outro("Setup complete!");
}
